// src/pages/ContactUs.jsx
import { useMemo, useRef, useState } from 'react';
import { WebLayout } from 'components/Layout';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Instagram,
  MessageCircle,
  Send,
  Copy,
  Check,
  AlertCircle,
} from 'react-feather';

const CONTACT = {
  addressLine: 'Najjera, Kampala, Uganda',
  phoneDisplay: '+256 782 692271',
  phoneDial: '+256782692271', // no spaces
  email: 'hello@gambitgames.ug',
  instagram: 'https://instagram.com/gambit_games_ug',
  telegram: 'https://t.me/gambit_games_ug',
  whatsappNumber: '256782692271', // no + sign
  hours: [
    { d: 'Mon–Fri', h: '09:00 – 18:00' },
    { d: 'Sat', h: '10:00 – 16:00' },
    { d: 'Sun', h: 'Closed' },
  ],
};

export function ContactUs() {
  // Parallax hero
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0.85]);

  // Form state
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    topic: 'General Inquiry',
    message:
      "Hi Gambit Games! I'd love to ask about your wooden board games. Please get back to me. 😊",
    prefer: 'whatsapp', // whatsapp | email | instagram | telegram
  });

  const [errors, setErrors] = useState({});
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState('');

  const topics = useMemo(
    () => [
      'General Inquiry',
      'Custom Order',
      'Bulk/Corporate',
      'Delivery & Lead Time',
      'After-Sales Support',
    ],
    []
  );

  const composed = useMemo(() => {
    const lines = [
      `Contact Topic: ${form.topic}`,
      `Name: ${form.name || '—'}`,
      `Phone: ${form.phone || '—'}`,
      `Email: ${form.email || '—'}`,
      `Preferred contact: ${form.prefer}`,
      `Message:`,
      form.message || '—',
      `— Sent from gambitgames.ug`,
    ];
    return lines.join('\n');
  }, [form]);

  const mailtoHref = useMemo(() => {
    const subject = encodeURIComponent(`Gambit Games — ${form.topic}`);
    const body = encodeURIComponent(composed);
    return `mailto:${CONTACT.email}?subject=${subject}&body=${body}`;
  }, [form, composed]);

  const whatsappHref = useMemo(() => {
    return `https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(composed)}`;
  }, [composed]);

  // Simple validation
  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Your name is required';
    if (!form.message.trim()) e.message = 'Please write a short message';
    if (form.prefer === 'email' && !/^\S+@\S+\.\S+$/.test(form.email)) {
      e.email = 'Valid email required for email contact';
    }
    if (form.prefer === 'whatsapp' && !form.phone.trim()) {
      // not strictly required, but helps them remember
      e.phone = 'Phone helps us reach you faster';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!validate()) {
      setToast('Please fix the highlighted fields.');
      return;
    }

    // Open the right channel
    if (form.prefer === 'whatsapp') {
      window.open(whatsappHref, '_blank', 'noopener,noreferrer');
      setToast('Opening WhatsApp…');
    } else if (form.prefer === 'email') {
      window.location.href = mailtoHref; // mailto: should open email client
      setToast('Opening your email app…');
    } else if (form.prefer === 'instagram') {
      window.open(CONTACT.instagram, '_blank', 'noopener,noreferrer');
      setToast('Opening Instagram…');
    } else if (form.prefer === 'telegram') {
      window.open(CONTACT.telegram, '_blank', 'noopener,noreferrer');
      setToast('Opening Telegram…');
    }
  };

  const copyMessage = async () => {
    try {
      await navigator.clipboard.writeText(composed);
      setCopied(true);
      setToast('Message copied to clipboard');
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setToast('Could not copy. Long-press to copy manually.');
    }
  };

  const chip = (active) =>
    `rounded-full px-3 py-1.5 text-sm ${
      active ? 'bg-[#12A2BF] text-white' : 'bg-gray-900 text-gray-200 hover:bg-gray-800'
    }`;

  return (
    <WebLayout>
      {/* HERO */}
      <section ref={heroRef} className="relative isolate overflow-hidden bg-gray-950">
        <motion.div style={{ y, opacity }} className="pointer-events-none absolute inset-0">
          <img
            src="/assets/contact_hero.jpg"
            alt=""
            className="h-full w-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-950/60 via-gray-950/60 to-gray-950" />
        </motion.div>

        <div className="relative mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#12A2BF]/30 bg-[#12A2BF]/10 px-3 py-1 text-xs font-medium text-[#12A2BF]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#12A2BF]" />
              Talk to us • Kampala
            </div>
            <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
              Contact Gambit Games
            </h1>
            <p className="mt-3 max-w-xl text-sm text-gray-300">
              Questions, custom ideas, bulk orders, or support—send a message and we’ll get back
              within working hours.
            </p>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 rounded-md bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
              >
                Shop Games
              </Link>
              <Link
                to="/custom"
                className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
              >
                Start a Custom Order
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            {/* Form */}
            <form
              className="space-y-6 rounded-2xl border border-gray-800 bg-gray-900 p-6"
              onSubmit={handleSubmit}
            >
              <h2 className="text-lg font-semibold text-white">Send us a message</h2>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-300">Your name *</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className={`mt-1 w-full rounded-md border bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none ${
                      errors.name ? 'border-red-500' : 'border-gray-800'
                    }`}
                    placeholder="e.g., Sarah Nakato"
                  />
                  {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-300">Phone (WhatsApp)</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                    className={`mt-1 w-full rounded-md border bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none ${
                      errors.phone ? 'border-red-500' : 'border-gray-800'
                    }`}
                    placeholder="+256 7xx xxx xxx"
                  />
                  {errors.phone && <p className="mt-1 text-xs text-red-400">{errors.phone}</p>}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <label className="block text-sm text-gray-300">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    className={`mt-1 w-full rounded-md border bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none ${
                      errors.email ? 'border-red-500' : 'border-gray-800'
                    }`}
                    placeholder="you@example.com"
                  />
                  {errors.email && <p className="mt-1 text-xs text-red-400">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-sm text-gray-300">Topic</label>
                  <select
                    value={form.topic}
                    onChange={(e) => setForm((f) => ({ ...f, topic: e.target.value }))}
                    className="mt-1 w-full rounded-md border border-gray-800 bg-gray-950 px-3 py-2 text-sm text-gray-100"
                  >
                    {topics.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300">Message *</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
                  className={`mt-1 w-full rounded-md border bg-gray-950 px-3 py-2 text-sm text-gray-100 placeholder-gray-500 focus:outline-none ${
                    errors.message ? 'border-red-500' : 'border-gray-800'
                  }`}
                  placeholder="Tell us what you need help with…"
                />
                {errors.message && <p className="mt-1 text-xs text-red-400">{errors.message}</p>}
              </div>

              {/* Preferred channel */}
              <div>
                <label className="block text-sm font-semibold text-white">Preferred contact</label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    { id: 'whatsapp', label: 'WhatsApp', icon: <MessageCircle size={14} /> },
                    { id: 'email', label: 'Email', icon: <Mail size={14} /> },
                    { id: 'instagram', label: 'Instagram', icon: <Instagram size={14} /> },
                    { id: 'telegram', label: 'Telegram', icon: <Send size={14} /> },
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, prefer: opt.id }))}
                      className={chip(form.prefer === opt.id)}
                    >
                      <span className="inline-flex items-center gap-1">
                        {opt.icon}
                        {opt.label}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="submit"
                  className="inline-flex items-center gap-2 rounded-md bg-[#12A2BF] px-4 py-2 text-sm font-semibold text-white hover:brightness-110"
                >
                  {form.prefer === 'whatsapp' ? (
                    <>
                      <MessageCircle size={16} /> Send via WhatsApp
                    </>
                  ) : form.prefer === 'email' ? (
                    <>
                      <Mail size={16} /> Send via Email
                    </>
                  ) : form.prefer === 'instagram' ? (
                    <>
                      <Instagram size={16} /> Open Instagram
                    </>
                  ) : (
                    <>
                      <Send size={16} /> Open Telegram
                    </>
                  )}
                </button>

                <a
                  href={mailtoHref}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
                  onClick={(e) => {
                    // allow quick email regardless of prefer
                    if (!validate()) e.preventDefault();
                  }}
                >
                  <Mail size={16} />
                  Quick email
                </a>

                <a
                  href={`tel:${CONTACT.phoneDial}`}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
                >
                  <Phone size={16} />
                  Call us
                </a>

                <button
                  type="button"
                  onClick={copyMessage}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? 'Copied' : 'Copy message'}
                </button>
              </div>

              <p className="mt-2 flex items-start gap-2 text-xs text-gray-400">
                <AlertCircle size={14} className="mt-0.5" />
                We currently deliver within Kampala. For special cases beyond Kampala, chat with us.
              </p>
            </form>

            {/* Contact details / Map */}
            <aside className="space-y-6">
              <div className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
                <h2 className="text-lg font-semibold text-white">Visit & Contact</h2>
                <ul className="mt-3 space-y-2 text-sm text-gray-300">
                  <li className="flex items-start gap-2">
                    <MapPin size={16} className="mt-0.5 text-[#12A2BF]" />
                    <span>{CONTACT.addressLine}</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Phone size={16} className="mt-0.5 text-[#12A2BF]" />
                    <a href={`tel:${CONTACT.phoneDial}`} className="hover:text-white">
                      {CONTACT.phoneDisplay}
                    </a>
                  </li>
                  <li className="flex items-start gap-2">
                    <Mail size={16} className="mt-0.5 text-[#12A2BF]" />
                    <a href={`mailto:${CONTACT.email}`} className="hover:text-white">
                      {CONTACT.email}
                    </a>
                  </li>
                </ul>

                <div className="mt-4 grid gap-2 sm:grid-cols-3">
                  <a
                    href={`https://wa.me/${CONTACT.whatsappNumber}?text=${encodeURIComponent(
                      'Hi Gambit Games! I have a quick question.'
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#12A2BF] px-3 py-2 text-sm font-semibold text-black hover:brightness-110"
                  >
                    <MessageCircle size={16} />
                    WhatsApp
                  </a>
                  <a
                    href={CONTACT.instagram}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-pink-500/90 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                  >
                    <Instagram size={16} />
                    Instagram
                  </a>
                  <a
                    href={CONTACT.telegram}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-sky-500 px-3 py-2 text-sm font-semibold text-white hover:brightness-110"
                  >
                    <Send size={16} />
                    Telegram
                  </a>
                </div>

                <div className="mt-6 rounded-xl border border-gray-800 bg-gray-950 p-4">
                  <h3 className="text-sm font-semibold text-white">Business Hours</h3>
                  <ul className="mt-2 space-y-1 text-xs text-gray-300">
                    {CONTACT.hours.map((h) => (
                      <li key={h.d} className="flex items-center justify-between">
                        <span className="inline-flex items-center gap-2">
                          <Clock size={14} className="text-[#12A2BF]" />
                          {h.d}
                        </span>
                        <span>{h.h}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Map embed */}
              <div className="overflow-hidden rounded-2xl border border-gray-800">
                <div className="h-64 w-full bg-gray-800">
                  <iframe
                    title="Map — Najjera, Kampala"
                    src="https://www.google.com/maps?q=Najjera,+Kampala,+Uganda&output=embed"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
                <div className="bg-gray-900 p-3 text-right text-xs text-gray-400">
                  <a
                    href="https://www.google.com/maps?q=Najjera,+Kampala,+Uganda"
                    target="_blank"
                    rel="noreferrer"
                    className="text-green-300 underline"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-6 left-1/2 z-[90] -translate-x-1/2 rounded-full bg-black/80 px-4 py-2 text-xs text-white"
            onAnimationComplete={() => {
              // auto clear after 2.5s
              setTimeout(() => setToast(''), 2500);
            }}
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </WebLayout>
  );
}
